import {
    FunctionComponent,
    useCallback,
    useMemo,
    useState,
    useEffect,
} from 'react';
import { Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import {
    useGetBillingsQuery,
    filterBillingsForApp,
    Billing,
    hasEmptyBillingData,
} from '@starjack/entities/billing';
import { useStarjackConfig } from '@starjack/core';
import { useAuthentication } from '@starjack/features/authentication';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder, formatStarjackDate } from '@starjack/utils';
import { StarjackTable } from '@starjack/shared-components';
import { Theme } from '@mui/material/styles';

const downloadContentGeneric = (filename: string, content: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', content);
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

// Extended theme interface for widgets that expect app-specific theme properties
interface ExtendedTheme extends Theme {
    page: {
        verticalSpacing: number;
        backgroundColor: string;
        template: {
            base: {
                paddingX: number;
                paddingY: number;
            };
        };
    };
}

type BillingsContentProps =
    | { variant: 'admin'; onBillingSelect?: never }
    | { variant: 'backoffice'; onBillingSelect: (billing: Billing) => void };

export const BillingsContent: FunctionComponent<BillingsContentProps> = ({
    variant,
    onBillingSelect,
}) => {
    const { application } = useStarjackConfig();
    const { liftOperatorData } = useAuthentication();
    const { data, isLoading } = useGetBillingsQuery({
        liftOperatorId: liftOperatorData?.liftOperatorId || '',
    });
    const billings = useMemo(
        () =>
            filterBillingsForApp(
                data ?? [],
                application,
                liftOperatorData?.liftOperatorId ?? ''
            ),
        [data, application, liftOperatorData?.liftOperatorId]
    );
    const theme = useTheme() as ExtendedTheme;

    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const currentItems = useMemo(() => {
        if (!billings.length) return [];
        return billings.slice(
            currentPage * itemsPerPage,
            currentPage * itemsPerPage + itemsPerPage
        );
    }, [billings, currentPage, itemsPerPage]);

    useEffect(() => {
        if (currentPage !== 0 && billings.length) {
            setCurrentPage(0);
        }
    }, [billings]);

    const getBillingExcel = useCallback(
        async (
            billingRunId: string,
            liftOperatorId: string
        ): Promise<string | null> => {
            const response = await fetchAuthWrapper(
                `${urlBuilder(
                    'order',
                    'billing'
                )}/${billingRunId}/${liftOperatorId}/billing.xlsx`,
                {
                    headers: {
                        Accept: '*',
                        'Content-Type':
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    },
                    method: 'GET',
                }
            );

            if (!response.ok) {
                return null;
            }

            return URL.createObjectURL(await response.blob());
        },
        []
    );

    const onDownloadClick = useCallback(
        (billing: Billing) => async () => {
            if (!liftOperatorData?.liftOperatorId) return;

            const response = await getBillingExcel(
                billing.id,
                liftOperatorData.liftOperatorId
            );

            if (response) {
                downloadContentGeneric(
                    `Abrechnung ${formatStarjackDate(
                        billing.billingPeriodFrom
                    )} - ${formatStarjackDate(billing.billingPeriodTo)}.xlsx`,
                    response
                );
            }
        },
        [liftOperatorData?.liftOperatorId, getBillingExcel]
    );

    const adminColumns = [
        {
            label: 'Abrechnung von',
            itemTransform: (billing: Billing) =>
                formatStarjackDate(billing.billingPeriodFrom),
        },
        {
            label: 'Abrechnung bis',
            itemTransform: (billing: Billing) =>
                formatStarjackDate(billing.billingPeriodTo),
        },
        {
            label: 'Aktion',
            itemTransform: (billing: Billing) => (
                <IconButton
                    onClick={onDownloadClick(billing)}
                    disabled={hasEmptyBillingData(
                        billing,
                        liftOperatorData?.liftOperatorId
                    )}
                >
                    <SvgIcon>
                        <DownloadIcon />
                    </SvgIcon>
                </IconButton>
            ),
        },
    ];

    const backofficeColumns = [
        {
            label: 'Erstellt am',
            itemTransform: (billing: Billing) =>
                formatStarjackDate(billing.createdAt),
        },
        {
            label: 'Abrechnung von',
            itemTransform: (billing: Billing) =>
                formatStarjackDate(billing.billingPeriodFrom),
        },
        {
            label: 'Abrechnung bis',
            itemTransform: (billing: Billing) =>
                formatStarjackDate(billing.billingPeriodTo),
        },
        {
            label: 'Freigegeben am',
            itemTransform: (billing: Billing) =>
                billing.releasedAt
                    ? formatStarjackDate(billing.releasedAt)
                    : '-',
        },
        {
            label: 'Freigegeben von',
            itemTransform: (billing: Billing) =>
                billing.releasedBy ? String(billing.releasedBy) : '-',
        },
        {
            label: 'Rechnungen Erfolg',
            itemTransform: (billing: Billing) =>
                billing.successLiftOperatorIds.length,
            options: {
                rowCellProps: {
                    align: 'right' as const,
                },
            },
        },
        {
            label: 'Rechnungen Fehler',
            itemTransform: (billing: Billing) =>
                billing.errorLiftOperatorIds.length,
            options: {
                rowCellProps: {
                    align: 'right' as const,
                },
            },
        },
        {
            label: 'Details',
            options: {
                rowCellProps: {
                    align: 'right' as const,
                },
            },
            itemTransform: (billing: Billing) => (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onBillingSelect?.(billing);
                    }}
                >
                    <SvgIcon>
                        <ArrowCircleRightOutlinedIcon />
                    </SvgIcon>
                </IconButton>
            ),
        },
    ];

    const columns = variant === 'admin' ? adminColumns : backofficeColumns;

    return (
        <>
            <Typography sx={{ mb: 8 }} variant='h4'>
                Abrechnungen
            </Typography>
            <Stack alignItems='flex-start' spacing={theme.page.verticalSpacing}>
                <StarjackTable<Billing>
                    count={billings.length}
                    items={currentItems}
                    columns={columns}
                    currentPage={currentPage}
                    onPageChange={(nextPage) => setCurrentPage(nextPage)}
                    itemId='id'
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    loading={isLoading}
                    {...(variant === 'backoffice' && onBillingSelect
                        ? {
                              onRowClick: (rowItem: Billing) =>
                                  onBillingSelect(rowItem),
                          }
                        : {})}
                />
            </Stack>
        </>
    );
};
